// Salesperson Analytics Page

import React, { useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import MainLayout from '../components/layout/MainLayout.jsx';
import InquiryDayList from '../components/inquiries/InquiryDayList.jsx';
import {
  fetchSalespersonAnalytics,
  clearAnalytics,
} from '../redux/slices/salespersonAnalyticsSlice';
import {
  FiArrowLeft,
  FiUsers,
  FiClipboard,
  FiTrendingUp,
  FiTarget,
  FiPhone,
  FiMail,
  FiRefreshCw,
  FiCheckCircle,
  FiUser,
  FiCalendar,
  FiSun,
  FiXCircle,
  FiDollarSign,
  FiClock,
  FiPauseCircle,
  FiCornerUpLeft,
  FiDownload,
  FiChevronDown,
  FiPrinter,
  FiActivity,
  FiAlertCircle,
  FiTag,
  FiX,
  FiPhoneCall,
  FiUserPlus,
  FiRotateCcw,
  FiBox,
} from 'react-icons/fi';

// Attendance status → badge styling + label
const ATT_STATUS = {
  present: { label: 'Present', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', Icon: FiCheckCircle },
  leave: { label: 'Leave', text: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', dot: 'bg-rose-500', Icon: FiXCircle },
  off: { label: 'Off', text: 'text-slate-500', bg: 'bg-slate-100', border: 'border-slate-200', dot: 'bg-slate-400', Icon: FiSun },
};

const fmtHours = (mins) => {
  if (mins == null) return null;
  return mins >= 60 ? `${(mins / 60).toFixed(1)} hr` : `${Math.round(mins)} min`;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(n ?? 0);

const pct = (n) => `${(n ?? 0).toFixed(1)}%`;

// ─── Period selector ──────────────────────────────────────────────────────────
const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week',  label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'year',  label: 'Year' },
];

// ─── Deal stages — the honest "what happened" for every inquiry ────────────────
const STAGES = [
  { key: 'won',     label: 'Sold',    tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'live',    label: 'Live',    tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  { key: 'pending', label: 'Pending', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'waiting', label: 'Waiting', tone: 'bg-purple-50 text-purple-700 border-purple-200' },
  { key: 'hold',    label: 'On Hold', tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  { key: 'loss',    label: 'Lost',    tone: 'bg-rose-50 text-rose-700 border-rose-200' },
];

const money = (v) => Number(v ?? 0);

// ─── Excel sheet builders ─────────────────────────────────────────────────────
// Each builder returns [sheetName, worksheet]. The "Full report" workbook is
// simply all of them; every Download-menu option is one (or a few) of them in
// its own file — so a new sheet automatically becomes a new download option.
const STAGE_LABEL = { pending: 'Pending', live: 'Live', waiting: 'Waiting', hold: 'On Hold', won: 'Sold', loss: 'Lost' };

const aoa = (rows) => XLSX.utils.aoa_to_sheet(rows);

function sheetSummary(data, period) {
  const sp = data?.salesperson || {};
  const st = data?.stages || {};
  const sv = data?.sales_value?.period || {};
  const rev = data?.revenue || {};
  const cus = data?.customers || {};
  const inq = data?.inquiries || {};
  const fu = data?.follow_ups || {};
  const disc = data?.discounts || {};
  const qw = data?.quote_wait || {};
  const src = data?.source_split || {};
  const rs = data?.restock_summary || {};
  return ['Summary', aoa([
    ['Salesperson', sp.name || ''],
    ['Phone', sp.phone_number || ''],
    ['Joined', sp.joined || ''],
    ['Period', period],
    ['Generated', data?.generated_at || ''],
    [],
    ['Unique customers', cus.unique_in_period ?? 0],
    ['Customers acquired (created by them)', cus.acquired_in_period ?? 0],
    ['Total inquiries', inq.total ?? 0],
    ['Conversion rate (%)', inq.conversion_rate_pct ?? 0],
    ['Avg wait for supervisor price (min)', qw.avg_minutes ?? ''],
    [],
    ['— Deals by stage —'],
    ['Sold', st.won ?? 0], ['Live', st.live ?? 0], ['Pending', st.pending ?? 0],
    ['Waiting', st.waiting ?? 0], ['On hold', st.hold ?? 0], ['Lost', st.loss ?? 0],
    [],
    ['— Money (this period) —'],
    ['Sold value (incl. money not yet paid)', money(sv.gross_value)],
    ['GIVEN TO COMPANY (collected)', money(sv.collected_value)],
    ['Balance still with customers', money(sv.pending_value)],
    ['On hold (awaiting admin)', money(sv.hold_value)],
    ['Returns / refunds', money(sv.returns_value)],
    [],
    ['Team collected', money(rev.team_collected)],
    ['% of team collected', rev.collected_pct_of_team ?? 0],
    ['Team sold value', money(rev.overall)],
    ['% of team sold value', rev.percentage_of_total ?? 0],
    [],
    ['— Discounts —'],
    ['Asked', disc.asked ?? 0], ['Approved', disc.approved ?? 0],
    ['Countered', disc.countered ?? 0], ['Rejected', disc.rejected ?? 0],
    ['Given away (₹)', disc.given_away_total ?? 0],
    [],
    ['— Sold parts by source —'],
    ['CAP (count / value)', `${src.cap?.count ?? 0} / ${src.cap?.value ?? 0}`],
    ['OSM (count / value)', `${src.osm?.count ?? 0} / ${src.osm?.value ?? 0}`],
    ['Outside stock (count / value)', `${src.stock?.count ?? 0} / ${src.stock?.value ?? 0}`],
    [],
    ['Follow-ups pending', fu.pending ?? 0],
    ['Follow-ups overdue', fu.overdue ?? 0],
    ['Restock calls', rs.total ?? 0],
    ['Restock calls converted', rs.converted ?? 0],
  ])];
}

// Quick-range buttons over the daily table
const DAILY_RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'week',  label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'year',  label: 'Year' },
];

const DAILY_HEADER = ['Date', 'Inquiries', 'Live', 'Waiting', 'Sold', 'Lost',
  'Revenue', 'Pending', 'On hold', 'Follow-ups'];
const dailyRow = (d) => [d.date, d.attended, d.live, d.waiting, d.won, d.loss,
  Number(d.revenue || 0), d.pending, d.hold, d.follow_ups];

function sheetDaily(data, rows) {
  const daily = rows || data?.daily_activity || [];
  return ['Daily activity', aoa([DAILY_HEADER, ...daily.map(dailyRow)])];
}

/** Download just the days handed in — the rows the admin ticked, or all of them. */
function exportDays(data, rows) {
  const name = (data?.salesperson?.name || 'salesperson').replace(/\s+/g, '_');
  const wb = XLSX.utils.book_new();
  const [sheetName, ws] = sheetDaily(data, rows);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const span = rows.length === 1 ? rows[0].date : `${rows.length}_days`;
  XLSX.writeFile(wb, `${name}_daily_${span}.xlsx`);
}

// The wide inquiry row — every column the backend now exports, so any stage
// sheet answers money + timing questions on its own.
// "First price" sits next to "Price" so the give-away is a subtraction the
// reader can do in one glance, and the three supervisor columns follow it —
// who set the number, who corrected it, and the whole chain when more than
// two people touched it.
const INQ_HEADER = ['Date', 'Customer', 'Phone', 'Location', 'Car', 'Parts', 'Qty',
  'Price', 'First price', 'Priced by', 'Corrected by', 'All supervisors', 'Priced at',
  'Part sources', 'Stage', 'Status', 'Priority', 'Payment mode', 'Payment type',
  'Advance', 'Final', 'Payment confirmed', 'Dispatched', 'Delivery address',
  'Discount', 'Last follow-up', 'Next follow-up'];
const inqRow = (r) => [
  r.created_at, r.customer_name, r.customer_phone, r.customer_location,
  r.car, r.parts, r.parts_count,
  r.quoted_price, r.first_quoted_price || '',
  r.quoted_by || '', r.quote_edited_by || '', r.supervisors || '', r.quoted_at || '',
  r.parts_sources || '',
  STAGE_LABEL[r.stage] || r.stage, r.status,
  r.priority || '', r.payment_mode || '', r.payment_type || '', r.advance_amount || '',
  r.final_amount || '', r.payment_confirmed_at || '', r.dispatched_at || '',
  r.dispatch_address || '', r.discount_outcome || '', r.latest_follow_up || '', r.next_follow_up_at || '',
];
function sheetsInquiries(data) {
  const detail = data?.inquiries_detail || [];
  const make = (list) => aoa([INQ_HEADER, ...list.map(inqRow)]);
  return [
    ['Inquiries', make(detail)],
    ['Live', make(detail.filter((r) => r.stage === 'live'))],
    ['Waiting', make(detail.filter((r) => r.stage === 'waiting'))],
    ['Sold', make(detail.filter((r) => r.stage === 'won'))],
    ['Lost', make(detail.filter((r) => r.stage === 'loss'))],
  ];
}

function sheetCustomers(data) {
  const roster = data?.customers?.roster || [];
  return ['Customers', aoa([
    ['Customer', 'Phone', 'Location', 'Inquiries', 'Sold', 'Sold value', 'Given to company',
      'Balance owed', 'Refunded', 'Last activity', 'Acquired by them'],
    ...roster.map((c) => [c.name, c.phone, c.location, c.inquiries, c.sold_deals ?? c.sold,
      c.sold_value ?? c.revenue, c.collected ?? '', c.balance ?? '', c.returns_value ?? '',
      c.last_activity, c.acquired ? 'Yes' : '']),
  ])];
}

/** Money by customer — what each one bought, paid, and still owes. */
function sheetCustomerMoney(data, period, scope = 'period') {
  const rows = data?.customer_money?.[scope] || [];
  const total = (k) => rows.reduce((a, r) => a + Number(r[k] || 0), 0);
  return [scope === 'overall' ? 'Money (all time)' : 'Money by customer', aoa([
    [`Money by customer — ${scope === 'overall' ? 'all time' : `this ${period}`}`],
    ['Sold value = what they bought · Given to company = cash actually received · Balance = still to collect'],
    [],
    ['Customer', 'Phone', 'Location', 'Deals sold', 'Sold value', 'Given to company',
      'Balance owed', 'Deals unpaid', 'Refunded', 'Last sale'],
    ...rows.map((r) => [r.name, r.phone, r.location, r.sold_count, r.sold_value,
      r.collected, r.balance, r.due_count, r.returns_value, r.last_sold_at || '']),
    [],
    ['TOTAL', '', '', total('sold_count'), total('sold_value'), total('collected'),
      total('balance'), total('due_count'), total('returns_value'), ''],
  ])];
}

function sheetFollowUps(data) {
  const records = data?.follow_ups?.records || [];
  return ['Follow-ups', aoa([
    ['Date', 'Customer', 'Phone', 'Car', 'Outcome', 'Note', 'Next follow-up'],
    ...records.map((r) => [r.created_at, r.customer_name, r.customer_phone, r.car,
      r.outcome, r.note, r.next_follow_up_at || '']),
  ])];
}

function sheetReturns(data) {
  const records = data?.returns_records || [];
  return ['Returns', aoa([
    ['Date', 'Customer', 'Car', 'Product', 'Qty', 'Type', 'Refund', 'Status', 'Cash paid', 'Resolved'],
    ...records.map((r) => [r.created_at, r.customer_name, r.car, r.product, r.quantity,
      r.type, r.refund_amount, r.status, r.cash_paid ? 'Yes' : '', r.resolved_at]),
  ])];
}

function sheetRestocks(data) {
  const records = data?.restock_calls || [];
  return ['Restock calls', aoa([
    ['Date', 'Part', 'Customer', 'Phone', 'Status', 'Converted', 'Final price', 'Notes'],
    ...records.map((r) => [r.created_at, r.part, r.customer_name, r.customer_phone,
      r.status, r.converted ? 'Yes' : '', r.final_price, r.notes]),
  ])];
}

function sheetNotAvailable(data) {
  const records = data?.unavailable_reports?.records || [];
  return ['Not available', aoa([
    ['Date', 'Supervisor', 'Supervisor message', 'Salesperson', 'Customer', 'Phone', 'Car', 'Parts', 'Note'],
    ...records.map((r) => [r.created_at, r.supervisor, r.message, r.salesperson,
      r.customer_name, r.customer_phone, r.car, r.parts, r.note]),
  ])];
}

function sheetMonthly(data) {
  const trend = data?.monthly_trend || [];
  return ['Monthly trend', aoa([
    ['Month', 'Inquiries', 'Sold', 'Revenue'],
    ...trend.map((m) => [m.label, m.inquiries, m.sold, m.revenue]),
  ])];
}

function downloadWorkbook(data, sheets, suffix) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(([name, ws]) => XLSX.utils.book_append_sheet(wb, ws, name));
  const name = (data?.salesperson?.name || 'salesperson').replace(/\s+/g, '_');
  XLSX.writeFile(wb, `${name}_${suffix}.xlsx`);
}

/** One stage on its own — "just the Waiting list, nothing else". */
function sheetOneStage(data, stage, label) {
  const rows = (data?.inquiries_detail || []).filter((r) => r.stage === stage);
  return [label, aoa([INQ_HEADER, ...rows.map(inqRow)])];
}

// The Download-menu options — label + which sheets each file carries.
// `group` splits the menu so "everything" and "one stage only" don't blur.
const EXPORTS = [
  { key: 'full', group: 'all', label: 'Full report (everything)', hint: 'All sheets in one workbook',
    build: (d, p) => [sheetSummary(d, p), sheetCustomerMoney(d, p), sheetCustomerMoney(d, p, 'overall'),
      sheetDaily(d), ...sheetsInquiries(d), sheetCustomers(d),
      sheetFollowUps(d), sheetReturns(d), sheetRestocks(d), sheetNotAvailable(d), sheetMonthly(d)],
    suffix: (p) => `full_report_${p}` },
  { key: 'money', group: 'all', label: 'Money by customer', hint: 'Sold · given to company · balance owed',
    build: (d, p) => [sheetCustomerMoney(d, p), sheetCustomerMoney(d, p, 'overall')],
    suffix: (p) => `customer_money_${p}` },
  { key: 'daily', group: 'all', label: 'Daily activity', hint: 'Per-day counts for the picked range',
    build: (d) => [sheetDaily(d)], suffix: () => 'daily_activity' },
  { key: 'inquiries', group: 'all', label: 'Inquiries by stage', hint: 'All + Live/Waiting/Sold/Lost sheets',
    build: (d) => sheetsInquiries(d), suffix: (p) => `inquiries_${p}` },

  // ONE stage per file — when the admin only wants the waiting list
  { key: 'only_live', group: 'stage', label: 'Live only', hint: 'Priced, with the customer',
    build: (d) => [sheetOneStage(d, 'live', 'Live')], suffix: (p) => `live_${p}` },
  { key: 'only_waiting', group: 'stage', label: 'Waiting only', hint: 'Parked for stock',
    build: (d) => [sheetOneStage(d, 'waiting', 'Waiting')], suffix: (p) => `waiting_${p}` },
  { key: 'only_pending', group: 'stage', label: 'Pending only', hint: 'Awaiting a supervisor price',
    build: (d) => [sheetOneStage(d, 'pending', 'Pending')], suffix: (p) => `pending_${p}` },
  { key: 'only_hold', group: 'stage', label: 'On hold only', hint: 'Confirmed, payment unverified',
    build: (d) => [sheetOneStage(d, 'hold', 'On Hold')], suffix: (p) => `hold_${p}` },
  { key: 'only_won', group: 'stage', label: 'Sold only', hint: 'Paid and confirmed',
    build: (d) => [sheetOneStage(d, 'won', 'Sold')], suffix: (p) => `sold_${p}` },
  { key: 'only_loss', group: 'stage', label: 'Lost only', hint: 'Customer did not buy',
    build: (d) => [sheetOneStage(d, 'loss', 'Lost')], suffix: (p) => `lost_${p}` },

  { key: 'customers', group: 'other', label: 'Customers', hint: 'Per-customer money picture',
    build: (d) => [sheetCustomers(d)], suffix: (p) => `customers_${p}` },
  { key: 'followups', group: 'other', label: 'Follow-ups', hint: 'Every follow-up recorded',
    build: (d) => [sheetFollowUps(d)], suffix: () => 'followups' },
  { key: 'returns', group: 'other', label: 'Returns', hint: 'Returns on their sales',
    build: (d) => [sheetReturns(d)], suffix: () => 'returns' },
  { key: 'restocks', group: 'other', label: 'Restock calls', hint: 'Call-backs and conversions',
    build: (d) => [sheetRestocks(d)], suffix: () => 'restock_calls' },
  { key: 'na', group: 'other', label: 'Not available', hint: 'Supervisor hand-offs',
    build: (d) => [sheetNotAvailable(d)], suffix: () => 'not_available' },
  { key: 'monthly', group: 'other', label: 'Monthly trend', hint: 'Last 6 months',
    build: (d) => [sheetMonthly(d)], suffix: () => 'monthly_trend' },
];

const EXPORT_GROUPS = [
  { id: 'all', label: '' },
  { id: 'stage', label: 'One stage only' },
  { id: 'other', label: 'Other lists' },
];

function runExport(data, period, opt) {
  downloadWorkbook(data, opt.build(data, period), opt.suffix(period));
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, sub, valueColor = 'text-slate-900' }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-start gap-4">
    <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center shrink-0`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-slate-400 font-medium mb-1">{label}</p>
      <p className={`text-2xl font-bold leading-none ${valueColor}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  </div>
);

// ─── Money Tile (sales & returns) ─────────────────────────────────────────────
const MONEY_TONE = {
  violet:  { bg: 'bg-violet-50',  color: 'text-violet-600',  val: 'text-violet-700' },
  emerald: { bg: 'bg-emerald-50', color: 'text-emerald-600', val: 'text-emerald-700' },
  amber:   { bg: 'bg-amber-50',   color: 'text-amber-600',   val: 'text-amber-700' },
  orange:  { bg: 'bg-orange-50',  color: 'text-orange-600',  val: 'text-orange-700' },
  rose:    { bg: 'bg-rose-50',    color: 'text-rose-600',    val: 'text-rose-700' },
};
const MoneyTile = ({ icon: Icon, tone = 'violet', label, value, sub }) => {
  const t = MONEY_TONE[tone] || MONEY_TONE.violet;
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${t.bg}`}>
        <Icon className={`w-4 h-4 ${t.color}`} />
      </div>
      <p className="text-[11px] text-slate-400 font-medium">{label}</p>
      <p className={`text-base font-bold leading-tight ${t.val}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
};

// ─── Skeleton loader ──────────────────────────────────────────────────────────
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-slate-100 rounded-xl ${className}`} />
);

const PageSkeleton = () => (
  <div className="space-y-5">
    <Skeleton className="h-24 w-full" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
    </div>
    <Skeleton className="h-32 w-full" />
    <Skeleton className="h-48 w-full" />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Skeleton className="h-48" />
      <Skeleton className="h-48" />
    </div>
    <Skeleton className="h-64 w-full" />
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const SalespersonAnalyticsPage = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const { data, loading, error } = useSelector((s) => s.salespersonAnalytics);

  // TODAY by default — same reason as the Daily Activity window below: the
  // superadmin opens this to run the day, not to read a month of history.
  const [period, setPeriod] = React.useState('today');
  // Daily Activity window — TODAY by default (they attend 200+ customers a
  // day; a 30-day wall of rows is unreadable). Superadmin widens it on demand.
  const [dailyRange, setDailyRange] = React.useState('today'); // today | week | month | year | custom
  const [customFrom, setCustomFrom] = React.useState('');
  const [customTo, setCustomTo] = React.useState('');
  // Attendance rows shown at once — dropdown instead of an endless scroll
  const [attWindow, setAttWindow] = React.useState(7);
  const [downloadOpen, setDownloadOpen] = React.useState(false);
  const downloadRef = React.useRef(null);
  // Rows the admin ticked — lets them export just the days they care about
  const [pickedDays, setPickedDays] = React.useState([]);
  // Money-by-customer table: this period vs all time, search, unpaid filter
  const [moneyScope, setMoneyScope] = React.useState('period'); // period | overall
  const [moneyQuery, setMoneyQuery] = React.useState('');
  const [dueOnly, setDueOnly] = React.useState(false);
  const [moneyAll, setMoneyAll] = React.useState(false);

  const dailyBounds = React.useMemo(() => {
    const p = (n) => String(n).padStart(2, '0');
    const iso = (d) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
    const today = new Date();
    if (dailyRange === 'today') return { from: iso(today), to: iso(today) };
    if (dailyRange === 'week') return { from: iso(daysAgo(6)), to: iso(today) };
    if (dailyRange === 'month') return { from: iso(daysAgo(29)), to: iso(today) };
    if (dailyRange === 'year') {
      // Backend caps a daily window at 92 days — a full year of rows is
      // unreadable anyway, so "Year" means the last quarter.
      return { from: iso(daysAgo(90)), to: iso(today) };
    }
    // custom — only a complete from→to range triggers a fetch
    return customFrom && customTo ? { from: customFrom, to: customTo } : null;
  }, [dailyRange, customFrom, customTo]);

  const load = useCallback(() => {
    dispatch(fetchSalespersonAnalytics({
      id,
      period,
      dailyFrom: dailyBounds?.from,
      dailyTo: dailyBounds?.to,
    }));
  }, [dispatch, id, period, dailyBounds]);

  useEffect(() => {
    // While a custom range is half-picked, keep showing the current data.
    // (This used to also clear the store on every dependency change, so
    // switching to "Custom" wiped the whole screen and never reloaded.)
    if (dailyRange === 'custom' && !dailyBounds) return;
    load();
  }, [load, dailyRange, dailyBounds]);

  // Drop the cached analytics ONLY when leaving the screen.
  useEffect(() => () => { dispatch(clearAnalytics()); }, [dispatch]);

  // Close the Download menu on an outside click or Escape. A full-screen
  // overlay cannot do this job here: the header bar carries a fade-in
  // animation, and a transformed ancestor makes `position: fixed` resolve
  // against the header instead of the viewport, so the overlay only ever
  // covered the header strip.
  useEffect(() => {
    if (!downloadOpen) return undefined;
    const onPointerDown = (e) => {
      if (!downloadRef.current?.contains(e.target)) setDownloadOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setDownloadOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [downloadOpen]);

  const sp  = data?.salesperson;
  const inq = data?.inquiries;
  const rev = data?.revenue;
  const cus = data?.customers;
  const sv  = data?.sales_value;   // gross · collected · pending · hold · returns

  // Daily Activity rows + range totals (footer when more than one day shows)
  const dailyAct = data?.daily_activity || [];
  const dailyTotals = dailyAct.reduce(
    (a, d) => ({
      attended: a.attended + (d.attended || 0),
      live: a.live + (d.live || 0),
      waiting: a.waiting + (d.waiting || 0),
      won: a.won + (d.won || 0),
      loss: a.loss + (d.loss || 0),
      revenue: a.revenue + Number(d.revenue || 0),
      follow_ups: a.follow_ups + (d.follow_ups || 0),
    }),
    { attended: 0, live: 0, waiting: 0, won: 0, loss: 0, revenue: 0, follow_ups: 0 },
  );

  // ── Money by customer ──────────────────────────────────────────────────────
  // Rows come straight from the backend already summing to the tiles above
  // them (sold value = paid + still-owed, refunds clear the balance first).
  const moneyRows = React.useMemo(
    () => data?.customer_money?.[moneyScope] || [],
    [data, moneyScope],
  );
  const moneyBlock = sv?.[moneyScope];
  const moneyFiltered = React.useMemo(() => {
    const q = moneyQuery.trim().toLowerCase();
    let rows = moneyRows;
    if (q) {
      rows = rows.filter((r) => (r.name || '').toLowerCase().includes(q)
        || (r.phone || '').includes(q));
    }
    if (dueOnly) rows = rows.filter((r) => Number(r.balance || 0) > 0);
    return rows;
  }, [moneyRows, moneyQuery, dueOnly]);
  const moneyTotals = React.useMemo(() => moneyFiltered.reduce((a, r) => ({
    sold_count: a.sold_count + (r.sold_count || 0),
    sold_value: a.sold_value + Number(r.sold_value || 0),
    collected: a.collected + Number(r.collected || 0),
    balance: a.balance + Number(r.balance || 0),
  }), { sold_count: 0, sold_value: 0, collected: 0, balance: 0 }), [moneyFiltered]);
  const dueCustomers = moneyRows.filter((r) => Number(r.balance || 0) > 0).length;
  const moneyShown = moneyAll ? moneyFiltered : moneyFiltered.slice(0, 8);

  // Attendance — newest-first from the backend; show only the chosen window
  const attAll = data?.attendance || [];
  const attRows = attAll.slice(0, attWindow);
  const attCounts = attRows.reduce((a, d) => { a[d.status] = (a[d.status] || 0) + 1; return a; }, {});

  return (
    <MainLayout>
      <div className="space-y-5 pb-8">

        {/* ── Header bar ─────────────────────────────────────────────────────
            `relative z-30`: every block on this page is animated with
            fade-in-up, and an animation that ends on a transform leaves the
            element a stacking context for good. Without an explicit z-index
            the later blocks (KPI cards, Conversion Rate…) paint over the
            header — taking the open Download menu with them. */}
        <div className="relative z-30 flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-in-up">
          {/* Back + identity */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => navigate('/salespersons')}
              className="w-9 h-9 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50 transition-all shrink-0"
            >
              <FiArrowLeft className="w-4 h-4" />
            </button>

            {loading && !sp ? (
              <div className="flex items-center gap-3">
                <Skeleton className="w-11 h-11 rounded-xl" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ) : sp ? (
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-md">
                  {sp.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-lg font-bold text-slate-900 truncate">{sp.name}</h1>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                      sp.is_active
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}>
                      {sp.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <FiPhone className="w-3 h-3" />{sp.phone_number}
                    </span>
                    {sp.email && (
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <FiMail className="w-3 h-3" />{sp.email}
                      </span>
                    )}
                    {sp.joined && (
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <FiCalendar className="w-3 h-3" />joined {String(sp.joined).split(' at ')[0]}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Period selector + refresh */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPeriod(p.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    period === p.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              onClick={load}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-400 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50 transition-all disabled:opacity-50"
            >
              <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {data && (
              <div className="flex items-center gap-2 no-print">
                {/* Download ▾ — one menu, one Excel file per option */}
                <div className="relative" ref={downloadRef}>
                  <button
                    onClick={() => setDownloadOpen((v) => !v)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-emerald-600 text-white text-xs font-semibold shadow-sm hover:bg-emerald-700 transition-all"
                  >
                    <FiDownload className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Download</span>
                    <FiChevronDown className="w-3 h-3" />
                  </button>
                  {downloadOpen && (
                    <div className="absolute right-0 z-40 mt-1 max-h-[70vh] w-64 overflow-y-auto rounded-xl border border-slate-100 bg-white py-1 shadow-xl">
                      {EXPORT_GROUPS.map((g) => (
                        <div key={g.id} className={g.id !== 'all' ? 'border-t border-slate-100' : ''}>
                          {g.label && (
                            <p className="px-3.5 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                              {g.label}
                            </p>
                          )}
                          {EXPORTS.filter((o) => o.group === g.id).map((opt) => (
                            <button
                              key={opt.key}
                              onClick={() => { setDownloadOpen(false); runExport(data, period, opt); }}
                              className="flex w-full flex-col items-start px-3.5 py-2 text-left transition hover:bg-emerald-50"
                            >
                              <span className={`text-xs font-bold ${opt.key === 'full' ? 'text-emerald-700' : 'text-slate-700'}`}>
                                {opt.label}
                              </span>
                              <span className="text-[10px] text-slate-400">{opt.hint}</span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => window.print()}
                  title="Print"
                  className="w-9 h-9 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-400 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50 transition-all"
                >
                  <FiPrinter className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* generated_at */}
        {data?.generated_at && (
          <p className="text-xs text-slate-400 -mt-2">
            Generated: {data.generated_at}
          </p>
        )}

        {/* ── Loading skeleton ──────────────────────────────────────────────── */}
        {loading && !data && <PageSkeleton />}

        {/* ── Error ────────────────────────────────────────────────────────── */}
        {error && !loading && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center">
            <p className="text-rose-700 font-semibold mb-1">Failed to load analytics</p>
            <p className="text-rose-500 text-sm mb-4">{error}</p>
            <button
              onClick={load}
              className="px-4 py-2 bg-rose-600 text-white text-sm font-semibold rounded-xl hover:bg-rose-700 transition-all"
            >
              Try Again
            </button>
          </div>
        )}

        {data && (
          <>
            {/* ── KPI cards ───────────────────────────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
              <StatCard
                icon={FiUsers}
                iconBg="bg-sky-50"
                iconColor="text-sky-500"
                label="Unique Customers"
                value={cus?.unique_in_period ?? 0}
                sub={`this ${period}`}
                valueColor="text-sky-700"
              />
              <StatCard
                icon={FiClipboard}
                iconBg="bg-violet-50"
                iconColor="text-violet-500"
                label="Total Inquiries"
                value={inq?.total ?? 0}
                sub={`this ${period}`}
                valueColor="text-violet-700"
              />
              {/* Money that actually REACHED the company — an advance deal's
                  unpaid balance is not revenue, so it is called out separately
                  instead of being folded into one "sold" number. */}
              <StatCard
                icon={FiTrendingUp}
                iconBg="bg-emerald-50"
                iconColor="text-emerald-500"
                label="Given to Company"
                value={fmt(rev?.collected)}
                sub={(rev?.pending ?? 0) > 0
                  ? `${fmt(rev?.this_person)} sold · ${fmt(rev?.pending)} still to collect`
                  : `${fmt(rev?.this_person)} sold · fully paid · this ${period}`}
                valueColor="text-emerald-700"
              />
              <StatCard
                icon={FiTarget}
                iconBg="bg-amber-50"
                iconColor="text-amber-500"
                label="Conversion Rate"
                value={pct(inq?.conversion_rate_pct)}
                sub="sold ÷ decided deals"
                valueColor="text-amber-700"
              />
            </div>

            {/* ── Money from customers — the first thing admin should see ───
                Sold value, what actually reached the company, what is still
                sitting with the customer, and then the same split per person. */}
            {moneyBlock && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.055s' }}>
                <div className="flex flex-wrap items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
                  <div>
                    <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                      <FiDollarSign className="w-4 h-4 text-slate-400" />
                      Money from Customers
                    </h2>
                    <p className="text-xs text-slate-400 mt-1">
                      What they sold · what actually reached the company · what is still with the customer.
                    </p>
                  </div>
                  <div className="no-print flex items-center gap-2">
                    <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1">
                      {[{ id: 'period', label: `This ${period}` }, { id: 'overall', label: 'All time' }].map((t) => (
                        <button
                          key={t.id}
                          onClick={() => { setMoneyScope(t.id); setMoneyAll(false); }}
                          className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                            moneyScope === t.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => runExport(data, period, EXPORTS.find((e) => e.key === 'money'))}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-all"
                    >
                      <FiDownload className="w-3.5 h-3.5" /> Excel
                    </button>
                  </div>
                </div>

                {/* The five money numbers, in the order the cash moves */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 px-5 py-4">
                  <MoneyTile icon={FiDollarSign} tone="violet" label="Sold Value"
                    value={fmt(Number(moneyBlock.gross_value))}
                    sub={`${moneyBlock.won_count ?? 0} deals · incl. unpaid`} />
                  <MoneyTile icon={FiTrendingUp} tone="emerald" label="Given to Company"
                    value={fmt(Number(moneyBlock.collected_value))} sub="cash actually received" />
                  <MoneyTile icon={FiClock} tone="amber" label="Balance With Customers"
                    value={fmt(Number(moneyBlock.pending_value))}
                    sub={`${moneyBlock.pending_count ?? 0} deals still to collect`} />
                  <MoneyTile icon={FiPauseCircle} tone="orange" label="On Hold"
                    value={fmt(Number(moneyBlock.hold_value))}
                    sub={`${moneyBlock.hold_count ?? 0} awaiting admin — not sold`} />
                  <MoneyTile icon={FiCornerUpLeft} tone="rose" label="Refunded Back"
                    value={fmt(Number(moneyBlock.returns_value))}
                    sub={`${moneyBlock.returns_count ?? 0} returns`} />
                </div>

                <p className="px-5 pb-3 text-[11px] text-slate-400">
                  Sold Value − Balance − Refunds = <b className="text-emerald-600">Given to Company</b>.
                  An advance deal is only counted for the money that came in; the rest stays in Balance until admin marks it collected.
                </p>

                {/* Per-customer split — the same figures, by name */}
                <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/60">
                  <span className="text-xs font-bold text-slate-600">By customer</span>
                  {dueCustomers > 0 && (
                    <button
                      onClick={() => setDueOnly((v) => !v)}
                      className={`no-print inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold border transition-all ${
                        dueOnly ? 'bg-amber-500 text-white border-amber-500'
                                : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                      }`}
                    >
                      <FiAlertCircle className="w-3 h-3" /> {dueCustomers} still owe money
                    </button>
                  )}
                  <div className="no-print relative ml-auto">
                    <input
                      value={moneyQuery}
                      onChange={(e) => setMoneyQuery(e.target.value)}
                      placeholder="Search customer or phone…"
                      className="w-56 pl-3 pr-7 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-200"
                    />
                    {moneyQuery && (
                      <button onClick={() => setMoneyQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <FiX className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {moneyFiltered.length === 0 ? (
                  <div className="px-5 py-10 text-center">
                    <FiUsers className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">
                      {moneyRows.length === 0
                        ? `No confirmed sales ${moneyScope === 'overall' ? 'yet' : `this ${period}`}.`
                        : 'No customer matches that search.'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 border-y border-slate-100">
                          <tr>
                            <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-2.5">Customer</th>
                            <th className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-2.5">Deals</th>
                            <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-2.5">Sold Value</th>
                            <th className="text-right text-xs font-semibold text-emerald-500 uppercase tracking-wider px-4 py-2.5">Given</th>
                            <th className="text-right text-xs font-semibold text-amber-500 uppercase tracking-wider px-4 py-2.5">Balance</th>
                            <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-2.5">Last Sale</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {moneyShown.map((r) => {
                            const due = Number(r.balance || 0) > 0;
                            return (
                              <tr key={r.customer_id} className={due ? 'bg-amber-50/50' : 'hover:bg-slate-50/60'}>
                                <td className="px-5 py-3">
                                  <p className="text-sm font-semibold text-slate-800">{r.name || '—'}</p>
                                  <p className="text-xs text-slate-400">
                                    {r.phone || '—'}{r.location ? ` · ${r.location}` : ''}
                                  </p>
                                </td>
                                <td className="px-4 py-3 text-center text-sm font-semibold text-slate-600">
                                  {r.sold_count || 0}
                                </td>
                                <td className="px-4 py-3 text-right text-sm font-bold text-slate-800">
                                  {fmt(r.sold_value)}
                                </td>
                                <td className="px-4 py-3 text-right text-sm font-bold text-emerald-600">
                                  {fmt(r.collected)}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {due ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-200 px-2 py-0.5 text-xs font-bold text-amber-700">
                                      {fmt(r.balance)}
                                      {r.due_count > 1 && <span className="font-medium">· {r.due_count} deals</span>}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400">
                                      <FiCheckCircle className="w-3.5 h-3.5 text-emerald-400" /> Cleared
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-xs text-slate-500">{r.last_sold_at || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                          <tr>
                            <td className="px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">
                              Total{(moneyQuery || dueOnly) ? ' (filtered)' : ''} · {moneyFiltered.length} customers
                            </td>
                            <td className="px-4 py-3 text-center text-sm font-bold text-slate-900">{moneyTotals.sold_count}</td>
                            <td className="px-4 py-3 text-right text-sm font-bold text-slate-900">{fmt(moneyTotals.sold_value)}</td>
                            <td className="px-4 py-3 text-right text-sm font-bold text-emerald-700">{fmt(moneyTotals.collected)}</td>
                            <td className="px-4 py-3 text-right text-sm font-bold text-amber-700">{fmt(moneyTotals.balance)}</td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    {moneyFiltered.length > 8 && (
                      <div className="no-print px-5 py-3 border-t border-slate-100 text-center">
                        <button
                          onClick={() => setMoneyAll((v) => !v)}
                          className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                        >
                          {moneyAll ? 'Show top 8 only' : `Show all ${moneyFiltered.length} customers`}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Working-style strip — how they operate, not just what they sold */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-up" style={{ animationDelay: '0.06s' }}>
              <StatCard
                icon={FiClock}
                iconBg="bg-slate-100"
                iconColor="text-slate-500"
                label="Wait for Price"
                value={data.quote_wait?.avg_minutes != null
                  ? (data.quote_wait.avg_minutes >= 60
                    ? `${(data.quote_wait.avg_minutes / 60).toFixed(1)} hr`
                    : `${Math.round(data.quote_wait.avg_minutes)} min`)
                  : '—'}
                sub="avg supervisor response on their inquiries"
              />
              <StatCard
                icon={FiTag}
                iconBg="bg-rose-50"
                iconColor="text-rose-500"
                label="Discounts Given"
                value={fmt(data.discounts?.given_away_total)}
                sub={`${data.discounts?.asked ?? 0} asked · ${data.discounts?.approved ?? 0} approved`}
                valueColor="text-rose-700"
              />
              <StatCard
                icon={FiPhoneCall}
                iconBg="bg-purple-50"
                iconColor="text-purple-500"
                label="Restock Calls"
                value={`${data.restock_summary?.converted ?? 0}/${data.restock_summary?.total ?? 0}`}
                sub="converted back into deals"
                valueColor="text-purple-700"
              />
              <StatCard
                icon={FiUserPlus}
                iconBg="bg-teal-50"
                iconColor="text-teal-500"
                label="Customers Acquired"
                value={cus?.acquired_in_period ?? 0}
                sub="created by them, not just served"
                valueColor="text-teal-700"
              />
            </div>

            {/* ── Revenue contribution ─────────────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
              {/* Cash-in against cash-in — comparing one rep's collected money
                  against the team's SOLD value would flatter or punish them
                  unfairly, so both sides of this bar are money received. */}
              <h2 className="text-sm font-bold text-slate-700 mb-4">
                Share of Money Collected
                <span className="ml-2 font-medium text-slate-400">this {period}</span>
              </h2>
              <div className="flex items-center justify-between mb-2 text-sm">
                <span className="font-semibold text-slate-800">{fmt(rev?.collected)}</span>
                <span className="text-slate-400">of {fmt(rev?.team_collected)} the team collected</span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-700"
                  style={{ width: `${Math.min(rev?.collected_pct_of_team ?? 0, 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-violet-600 font-bold">{pct(rev?.collected_pct_of_team)}</span>
                <span className="text-xs text-slate-400">
                  Rest of team: {fmt((rev?.team_collected ?? 0) - (rev?.collected ?? 0))}
                </span>
              </div>
              <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-400">
                On value sold (paid or not): {fmt(rev?.this_person)} of {fmt(rev?.overall)} · {pct(rev?.percentage_of_total)}
              </p>
            </div>

            {/* ── Deals by stage — one honest bucket per inquiry ───────────── */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 animate-fade-in-up" style={{ animationDelay: '0.10s' }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-slate-700">Deals by Stage</h2>
                <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                  {inq?.total ?? 0} inquiries · this {period}
                </span>
              </div>
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                {STAGES.map((s) => (
                  <div key={s.key} className={`rounded-xl border p-3 text-center ${s.tone}`}>
                    <p className="text-2xl font-bold leading-none">{data.stages?.[s.key] ?? 0}</p>
                    <p className="text-[11px] font-semibold mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
              {/* Follow-ups at a glance */}
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                  <FiClock className="w-3.5 h-3.5" /> Follow-ups
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-1 text-xs font-bold text-blue-700">
                  {data.follow_ups?.pending ?? 0} pending
                </span>
                {(data.follow_ups?.overdue ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2.5 py-1 text-xs font-bold text-rose-600">
                    <FiAlertCircle className="w-3 h-3" /> {data.follow_ups.overdue} overdue
                  </span>
                )}
                <span className="ml-auto text-xs text-slate-400 no-print">Use “Follow-ups” above to download the full log</span>
              </div>
            </div>

            {/* ── The inquiries themselves ─────────────────────────────────
                Every number above is a count of these rows; until now you
                could only reach them by downloading a spreadsheet. Same list,
                same reading as the Today screen — scoped to this person and
                following the period tabs at the top of the page. */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 animate-fade-in-up no-print" style={{ animationDelay: '0.11s' }}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <FiClipboard className="w-4 h-4 text-slate-400" />
                  <h2 className="text-sm font-bold text-slate-700">Inquiries they took</h2>
                </div>
                <span className="text-xs text-slate-400">
                  this {period} · biggest money first · who priced each one
                </span>
              </div>
              <InquiryDayList period={period} salespersonId={id} pageSize={15} />
            </div>

            {/* (The money tiles that used to sit here now open the page, next
                to the per-customer split — one money section, not two.) */}

            {/* ── Daily activity (last 30 days) — what they did each day ───── */}
            {dailyAct.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.115s' }}>
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <FiActivity className="w-4 h-4 text-slate-400" />
                    <h2 className="text-sm font-bold text-slate-700">Daily Activity</h2>
                    <span className="text-xs text-slate-400">
                      {dailyRange === 'today'
                        ? 'today'
                        : `${data.daily_window?.from ?? ''} → ${data.daily_window?.to ?? ''}`}
                    </span>
                  </div>
                  {/* Quick ranges + an always-visible from→to picker, so the
                      date fields never vanish behind a "Custom" mode. */}
                  <div className="no-print flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1">
                      {DAILY_RANGES.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => { setDailyRange(r.id); setCustomFrom(''); setCustomTo(''); }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            dailyRange === r.id
                              ? 'bg-white text-violet-700 shadow-sm'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2 py-1">
                      <input
                        type="date"
                        value={customFrom}
                        max={customTo || undefined}
                        onChange={(e) => { setCustomFrom(e.target.value); setDailyRange('custom'); }}
                        className="text-xs text-slate-600 bg-transparent focus:outline-none"
                      />
                      <span className="text-xs text-slate-300">→</span>
                      <input
                        type="date"
                        value={customTo}
                        min={customFrom || undefined}
                        onChange={(e) => { setCustomTo(e.target.value); setDailyRange('custom'); }}
                        className="text-xs text-slate-600 bg-transparent focus:outline-none"
                      />
                      {(customFrom || customTo) && (
                        <button
                          onClick={() => { setCustomFrom(''); setCustomTo(''); setDailyRange('today'); }}
                          className="text-slate-300 hover:text-rose-500"
                          title="Clear range"
                        >
                          <FiX className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => exportDays(data, pickedDays.length ? dailyAct.filter((d) => pickedDays.includes(d.date)) : dailyAct)}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-emerald-700"
                    >
                      <FiDownload className="w-3.5 h-3.5" />
                      {pickedDays.length ? `Excel (${pickedDays.length} day${pickedDays.length === 1 ? '' : 's'})` : 'Excel'}
                    </button>
                  </div>
                </div>

                {/* What the columns actually mean — the two halves are counted
                    differently and that has confused people before. */}
                <div className="px-5 pb-2 -mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="text-[11px] text-slate-400">
                    <b className="text-slate-500">Inquiries / Live / Waiting</b> — came in that day
                  </span>
                  <span className="text-[11px] text-slate-400">
                    <b className="text-slate-500">Sold / Lost / Revenue</b> — closed that day, whenever it started
                  </span>
                  {pickedDays.length > 0 && (
                    <button
                      onClick={() => setPickedDays([])}
                      className="ml-auto text-[11px] font-bold text-violet-600 hover:text-violet-700 no-print"
                    >
                      Clear {pickedDays.length} selected
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="w-10 px-3 py-3 no-print">
                          <input
                            type="checkbox"
                            checked={dailyAct.length > 0 && pickedDays.length === dailyAct.length}
                            onChange={(e) => setPickedDays(e.target.checked ? dailyAct.map((d) => d.date) : [])}
                            className="h-3.5 w-3.5 accent-violet-600"
                            title="Select every day"
                          />
                        </th>
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-2 py-3">Date</th>
                        <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Inquiries</th>
                        <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Live</th>
                        <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Waiting</th>
                        <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Sold</th>
                        <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Lost</th>
                        <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Revenue</th>
                        <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Follow-ups</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {[...dailyAct].reverse().map((d) => {
                        const empty = !d.attended && !d.follow_ups && !d.won && !d.loss;
                        const picked = pickedDays.includes(d.date);
                        return (
                          <tr
                            key={d.date}
                            onClick={() => setPickedDays((prev) =>
                              prev.includes(d.date) ? prev.filter((x) => x !== d.date) : [...prev, d.date])}
                            className={`cursor-pointer transition-colors ${
                              picked ? 'bg-violet-50' : 'hover:bg-slate-50/60'
                            } ${empty && !picked ? 'opacity-40' : ''}`}
                          >
                            <td className="px-3 py-3 no-print">
                              <input
                                type="checkbox"
                                checked={picked}
                                onChange={() => {}}
                                className="h-3.5 w-3.5 accent-violet-600 pointer-events-none"
                              />
                            </td>
                            <td className="px-2 py-3 text-sm font-semibold text-slate-700 whitespace-nowrap">{d.label}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-slate-700">{d.attended || <span className="text-slate-300">—</span>}</td>
                            <td className="px-4 py-3 text-sm text-right text-sky-600">{d.live || <span className="text-slate-300">—</span>}</td>
                            <td className="px-4 py-3 text-sm text-right text-purple-600">{d.waiting || <span className="text-slate-300">—</span>}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-emerald-700">{d.won || <span className="text-slate-300">—</span>}</td>
                            <td className="px-4 py-3 text-sm text-right text-rose-600">{d.loss || <span className="text-slate-300">—</span>}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-slate-800">{d.revenue ? fmt(d.revenue) : <span className="text-slate-300">—</span>}</td>
                            <td className="px-4 py-3 text-sm text-right text-blue-600">{d.follow_ups || <span className="text-slate-300">—</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {dailyAct.length > 1 && (
                      <tfoot>
                        <tr className="bg-slate-50 border-t border-slate-100">
                          <td className="no-print" />
                          <td className="px-2 py-3 text-sm font-bold text-slate-700">Total · {dailyAct.length} days</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-slate-800">{dailyTotals.attended}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-sky-700">{dailyTotals.live}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-purple-700">{dailyTotals.waiting}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-emerald-700">{dailyTotals.won}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-rose-600">{dailyTotals.loss}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-slate-900">{fmt(dailyTotals.revenue)}</td>
                          <td className="px-4 py-3 text-sm text-right font-bold text-blue-700">{dailyTotals.follow_ups}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            )}

            {/* ── "Not available" hand-offs (supervisor said it in chat) ───── */}
            {(data.unavailable_reports?.records?.length ?? 0) > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.118s' }}>
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <FiAlertCircle className="w-4 h-4 text-amber-500" />
                    <h2 className="text-sm font-bold text-slate-700">Not-available reports</h2>
                    <span className="text-xs text-slate-400">
                      supervisor said it in chat · {data.unavailable_reports.count} total
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Date</th>
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Supervisor</th>
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">What they said</th>
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Customer</th>
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 py-3">Parts</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {data.unavailable_reports.records.slice(0, 25).map((r, i) => (
                        <tr key={`${r.created_at}-${i}`} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-5 py-3 text-sm text-slate-600 whitespace-nowrap">{r.created_at}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-slate-700 whitespace-nowrap">{r.supervisor || '—'}</td>
                          <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate" title={r.message}>
                            {r.message ? `“${r.message}”` : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{r.customer_name || '—'}</td>
                          <td className="px-4 py-3 text-sm text-amber-700">{r.parts || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.unavailable_reports.records.length > 25 && (
                  <p className="px-5 py-2.5 text-xs text-slate-400 border-t border-slate-100">
                    Showing 25 of {data.unavailable_reports.records.length} — full list in the Excel export.
                  </p>
                )}
              </div>
            )}

            {/* ── Monthly trend + What they sell ───────────────────────────── */}
            <div className="grid lg:grid-cols-2 gap-5 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              {/* 6-month trend — spot a rep going up or down at a glance */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                  <h2 className="text-sm font-bold text-slate-700">Monthly Trend</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Last 6 months — all-time data, ignores the period filter</p>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-2.5">Month</th>
                      <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-2.5">Inquiries</th>
                      <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-2.5">Sold</th>
                      <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-2.5">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.monthly_trend || []).map((m) => (
                      <tr key={m.month} className="border-b border-slate-50 last:border-0">
                        <td className="px-5 py-2.5 text-sm font-semibold text-slate-700">{m.label}</td>
                        <td className="px-3 py-2.5 text-sm text-right text-slate-600">{m.inquiries}</td>
                        <td className="px-3 py-2.5 text-sm text-right font-bold text-emerald-600">{m.sold}</td>
                        <td className="px-5 py-2.5 text-sm text-right font-bold text-slate-900">{fmt(m.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* What they sell — parts + where the parts come from */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <FiBox className="w-4 h-4 text-slate-400" /> What They Sell
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['cap', 'osm', 'stock'].map((k) => {
                    const s = data.source_split?.[k];
                    const label = { cap: 'CAP', osm: 'OSM', stock: 'Outside stock' }[k];
                    return (
                      <span key={k} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-600">
                        {label}
                        <span className="text-slate-400 font-semibold">{s?.count ?? 0} sold · {fmt(s?.value)}</span>
                      </span>
                    );
                  })}
                </div>
                <p className="mt-4 mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Most-asked parts</p>
                {(data.top_parts || []).length === 0 ? (
                  <p className="text-xs text-slate-400">No parts yet in this period.</p>
                ) : (
                  <div className="space-y-1.5">
                    {(data.top_parts || []).slice(0, 6).map((p, i) => (
                      <div key={p.name + i} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                        <span className="truncate text-sm font-semibold text-slate-700">{p.name}</span>
                        <span className="shrink-0 text-xs font-bold text-slate-500">×{p.count}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(data.top_car_brands || []).length > 0 && (
                  <>
                    <p className="mt-4 mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Top brands</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(data.top_car_brands || []).slice(0, 8).map((b) => (
                        <span key={b.brand} className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">
                          {b.brand} · {b.count}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Returns + Restock calls ──────────────────────────────────── */}
            <div className="grid lg:grid-cols-2 gap-5 animate-fade-in-up" style={{ animationDelay: '0.11s' }}>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <FiRotateCcw className="w-4 h-4 text-rose-400" /> Returns on Their Sales
                  </h2>
                  <span className="text-xs font-bold text-slate-400">{(data.returns_records || []).length}</span>
                </div>
                {(data.returns_records || []).length === 0 ? (
                  <p className="px-5 py-8 text-center text-xs text-slate-400">No returns — clean record.</p>
                ) : (
                  <div className="divide-y divide-slate-50">
                    {(data.returns_records || []).slice(0, 6).map((r, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 px-5 py-2.5">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-700">
                            {r.product}<span className="font-normal text-slate-400"> · {r.customer_name}</span>
                          </p>
                          <p className="text-[11px] text-slate-400">{r.created_at} · {r.type === 'cash' ? 'cash refund' : 'exchange'} · {r.status}</p>
                        </div>
                        {r.refund_amount && <span className="shrink-0 text-sm font-bold text-rose-600">{fmt(r.refund_amount)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <FiPhoneCall className="w-4 h-4 text-purple-400" /> Restock Call-backs
                  </h2>
                  <span className="text-xs font-bold text-slate-400">
                    {data.restock_summary?.converted ?? 0}/{data.restock_summary?.total ?? 0} converted
                  </span>
                </div>
                {(data.restock_calls || []).length === 0 ? (
                  <p className="px-5 py-8 text-center text-xs text-slate-400">No restock calls routed to them yet.</p>
                ) : (
                  <div className="divide-y divide-slate-50">
                    {(data.restock_calls || []).slice(0, 6).map((r, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 px-5 py-2.5">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-700">
                            {r.part}<span className="font-normal text-slate-400"> · {r.customer_name}</span>
                          </p>
                          <p className="text-[11px] text-slate-400">{r.created_at}</p>
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${r.converted ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {r.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Top Customers + Top Car Models ───────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-fade-in-up" style={{ animationDelay: '0.12s' }}>

              {/* Top Customers */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <h2 className="text-sm font-bold text-slate-700 mb-4">
                  Top Customers
                  <span className="ml-2 text-xs font-medium text-slate-400">by inquiry count</span>
                </h2>
                {(cus?.top_customers?.length ?? 0) === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-6">No customer data</p>
                ) : (
                  <ol className="space-y-3">
                    {cus.top_customers.map((c, i) => (
                      <li key={c.id} className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          i === 0 ? 'bg-amber-100 text-amber-700' :
                          i === 1 ? 'bg-slate-100 text-slate-600' :
                          i === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-50 text-slate-400'
                        }`}>
                          {i + 1}
                        </span>
                        <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                          <FiUser className="w-3.5 h-3.5 text-sky-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                          <p className="text-xs text-slate-400">{c.phone_number}</p>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-violet-50 border border-violet-100 rounded-full shrink-0">
                          <FiClipboard className="w-2.5 h-2.5 text-violet-400" />
                          <span className="text-xs font-bold text-violet-600">{c.inquiry_count}</span>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* Top Car Models */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <h2 className="text-sm font-bold text-slate-700 mb-4">
                  Top Car Models
                  <span className="ml-2 text-xs font-medium text-slate-400">most inquired</span>
                </h2>
                {(data.top_car_models?.length ?? 0) === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-6">No model data</p>
                ) : (
                  <ol className="space-y-3">
                    {data.top_car_models.map((m, i) => {
                      const max = data.top_car_models[0]?.count || 1;
                      return (
                        <li key={m.car_model} className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            i === 0 ? 'bg-amber-100 text-amber-700' :
                            i === 1 ? 'bg-slate-100 text-slate-600' :
                            i === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-50 text-slate-400'
                          }`}>
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-semibold text-slate-800 truncate pr-2">{m.car_model}</p>
                              <span className="text-sm font-bold text-slate-700 shrink-0">{m.count}</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-indigo-400 to-violet-500 rounded-full"
                                style={{ width: `${(m.count / max) * 100}%` }}
                              />
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>

            {/* ── Attendance & Login History ───────────────────────────────── */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.14s' }}>
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <FiCalendar className="w-4 h-4 text-slate-400" />
                  <h2 className="text-sm font-bold text-slate-700">Attendance &amp; Login History</h2>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                  {/* Chips reflect the DAYS ON SCREEN, so they always match the table */}
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {attCounts.present || 0} Present
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 text-rose-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> {attCounts.leave || 0} Leave
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-slate-500">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> {attCounts.off || 0} Off
                  </span>
                  <select
                    value={attWindow}
                    onChange={(e) => setAttWindow(Number(e.target.value))}
                    className="no-print text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-violet-400"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                  </select>
                </div>
              </div>

              {attAll.length === 0 ? (
                <div className="py-10 text-center">
                  <FiCalendar className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">No attendance records yet</p>
                </div>
              ) : (
                <>
                  {/* Mobile: card list */}
                  <div className="lg:hidden divide-y divide-slate-50">
                    {attRows.map((day) => {
                      const meta = ATT_STATUS[day.status] || ATT_STATUS.leave;
                      const { Icon } = meta;
                      return (
                        <div key={day.date} className="flex items-center justify-between gap-3 px-5 py-3.5">
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-700">{day.label}</p>
                            <p className="text-xs text-slate-400">
                              {day.status === 'present'
                                ? <>In {day.first_login}{day.last_logout ? ` · Out ${day.last_logout}` : ' · active'}</>
                                : day.status === 'off' ? 'Weekly holiday' : 'No login recorded'}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {day.total_minutes != null && (
                              <span className="text-xs font-bold text-slate-600">{fmtHours(day.total_minutes)}</span>
                            )}
                            <span className={`inline-flex items-center gap-1 rounded-full ${meta.bg} border ${meta.border} px-2.5 py-1 text-xs font-bold ${meta.text}`}>
                              <Icon className="w-3 h-3" /> {meta.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop: table */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                          <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3">Date</th>
                          <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3">Day</th>
                          <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3">Status</th>
                          <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3">Login</th>
                          <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3">Logout</th>
                          <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3">Hours</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {attRows.map((day) => {
                          const meta = ATT_STATUS[day.status] || ATT_STATUS.leave;
                          const { Icon } = meta;
                          const rowBg = day.status === 'leave' ? 'bg-rose-50/30' : day.status === 'off' ? 'bg-slate-50/40' : '';
                          return (
                            <tr key={day.date} className={`hover:bg-slate-50/60 transition-colors ${rowBg}`}>
                              <td className="px-6 py-3.5 text-sm font-semibold text-slate-700 whitespace-nowrap">{day.label}</td>
                              <td className="px-6 py-3.5 text-sm text-slate-500 whitespace-nowrap">{day.weekday}</td>
                              <td className="px-6 py-3.5">
                                <span className={`inline-flex items-center gap-1.5 rounded-full ${meta.bg} border ${meta.border} px-2.5 py-1 text-xs font-bold ${meta.text}`}>
                                  <Icon className="w-3 h-3" /> {meta.label}
                                  {day.status === 'off' && day.worked && <span className="font-medium text-slate-400">(worked)</span>}
                                </span>
                              </td>
                              <td className="px-6 py-3.5 text-sm text-slate-600 whitespace-nowrap">
                                {day.first_login ?? <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-6 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                                {day.last_logout ?? (day.first_login ? <span className="text-emerald-500 font-medium">active</span> : <span className="text-slate-300">—</span>)}
                              </td>
                              <td className="px-6 py-3.5 text-sm font-semibold text-slate-700 whitespace-nowrap">
                                {day.total_minutes != null ? fmtHours(day.total_minutes) : <span className="text-slate-300">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}

      </div>
    </MainLayout>
  );
};

export default SalespersonAnalyticsPage;
